/**
 * P-1.8: the why-line's codes and fixes, a THIRD spelling of `authoring-web/src/why.ts`
 * (`WHY_CODES`, `WHY_FIXES`). `services/authoring-web/test/why-parity.test.ts` reads these
 * two frozen lists and the `WASLO.WHY` blocks of `i18n.js`, so a code added on one side and
 * forgotten on another fails a test, not a screen.
 */
import { frontendURL } from 'dashboard/helper/URLHelper';

export const WHY_CODES = Object.freeze([
  'task_step',
  'task_asked_for_person',
  'task_stuck',
  'task_call_failed',
  'task_broken',
  'task_system_refused',
  'task_changed_after_done',
  'task_cancel_after_done',
  'assistant_decided',
  'gate_withheld',
  'claim_withheld',
  'empty_reply',
  'no_credits',
  'spend_cap',
  'run_too_costly',
  'paused_by_waslo',
  'too_many_messages',
  'message_too_old',
  'busy_too_long',
  'unreadable_message',
  'send_uncertain',
  'partial_send',
  'channel_refused',
  'error',
  'assistant_off',
  'practice',
  'answer_when_open',
  'skip_label',
  'connection_failed',
  'task_asked',
  'task_finished',
  'answered_close',
  'answered_loose',
  'answered_none',
  'answered_plain',
  // Field report G2.2: a follow-up it sent, and the four reasons one was not sent.
  'follow_up_sent',
  'follow_up_person',
  'follow_up_window',
  'follow_up_hours',
  'follow_up_no_words',
]);

/** G2.2: a row that is a follow-up Waslo sent or dropped, not a turn of the customer's. */
export const isFollowUpWhy = why =>
  typeof why?.code === 'string' && why.code.startsWith('follow_up_');

export const WHY_FIXES = Object.freeze([
  'teach',
  'fix_task',
  'fix_personality',
  'request_top_up',
  'request_limit_raise',
  'check_connection',
  'turn_on',
]);

/** How close the best saved answer was. The BFF sends the word, never the score. */
export const CLOSENESS = Object.freeze(['close', 'loose', 'none']);

/** `WASLO.WHY.CODE.NO_CREDITS`, the sentence of one code. */
export const whyCopyKey = code =>
  `WASLO.WHY.CODE.${String(code).toUpperCase()}`;

/**
 * What pressing a why-line's fix does, for THIS viewer. Pure.
 *
 *   { kind: 'teach' }          open the teach dialog (administrator)
 *   { kind: 'suggest' }        open "Suggest this answer to the owner" (team member)
 *   { kind: 'link', to }       the page that fixes it (a path, like `AttentionRow`'s)
 *   { kind: 'ask', what }      ask us (`wasloApi.requestSetup`)
 *   { kind: 'turn_on' }        switch the assistant on for every chat
 *   { kind: 'ask_owner', fix } a sentence and no button (team member, P-1.6)
 *   null                       no fix
 */
export const whyAction = (why, { accountId, isAdmin }) => {
  if (!why || !WHY_FIXES.includes(why.fix)) return null;
  if (why.fix === 'teach') return { kind: isAdmin ? 'teach' : 'suggest' };
  if (!isAdmin) return { kind: 'ask_owner', fix: why.fix };
  if (accountId === null || accountId === undefined) return null;
  const at = path => frontendURL(`accounts/${accountId}/${path}`);
  const taskId = why.target?.taskId;
  const toolId = why.target?.toolId;
  switch (why.fix) {
    case 'fix_task':
      return {
        kind: 'link',
        to: at(
          Number.isSafeInteger(taskId) ? `waslo/tasks/${taskId}` : 'waslo/tasks'
        ),
      };
    case 'fix_personality':
      return { kind: 'link', to: at('waslo/personality') };
    case 'check_connection':
      return {
        kind: 'link',
        to: at(
          Number.isSafeInteger(toolId)
            ? `waslo/settings/connections/${toolId}`
            : 'waslo/settings/connections'
        ),
      };
    // Billing is manual: the top-up request lives on Overview (B2's button, P-5.3).
    case 'request_top_up':
      return { kind: 'link', to: at('waslo') };
    case 'request_limit_raise':
      return { kind: 'ask', what: 'limit_raise' };
    case 'turn_on':
      return { kind: 'turn_on' };
    default:
      return null;
  }
};
